import * as React from 'react';
import { enableBrowserNotificationsUrl } from '@/utils/urls';
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    forceEnableNotificationsBannerDismiss,
    getForceEnableBanner,
    getPermission,
    requestBrowserPermission,
} from '@/redux/modules/notification';
import { FormattedMessage } from 'react-intl';
import { getCookie, setCookie } from '@/redux/modules/cookies';
import { Text } from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useLocation } from 'react-router';
import { WarningIconSmall } from '@liveauctioneers/caterwaul-components/lib/Icons';
import Button from '@liveauctioneers/caterwaul-components/lib/Button/Button';
import ButtonLink from '@liveauctioneers/caterwaul-components/lib/ButtonLink/ButtonLink';
import notificationPermissions from '@/enums/notificationPermissions';
import styled, { css } from 'styled-components';

const COOKIE_MAX_AGE = 86400 * 365; // 365 days
const COOKIE_NAME = 'dismissableNotificationBanner';
const COOKIE_BODY = 'dismissed';

const DismissableGetWebNotificationsBanner = () => {
    const dispatch = useAppDispatch();
    const { pathname } = useLocation();
    const isCheckoutPage = pathname.includes('/checkout/');
    const biddingConsole = pathname.includes('/console/');
    const forceEnableBanner = useAppSelector(getForceEnableBanner);
    const permission = useAppSelector(getPermission);
    const dismissed = useAppSelector((state) => getCookie(state, COOKIE_NAME)) === COOKIE_BODY;

    const setCookieChecked = React.useCallback((): void => {
        dispatch(setCookie({ maxAge: COOKIE_MAX_AGE, name: COOKIE_NAME, value: COOKIE_BODY }));
        dispatch(forceEnableNotificationsBannerDismiss());
    }, [dispatch]);

    const onClickEnableNotifications = () => {
        dispatch(requestBrowserPermission());
    };

    if (biddingConsole || isCheckoutPage) {
        return null;
    }

    if (!forceEnableBanner) {
        if (permission === notificationPermissions.GRANTED || Boolean(dismissed)) {
            return null;
        }
    }

    const disallowedNotifications = permission === notificationPermissions.DENIED;

    return (
        <StyledBanner id="webNotificationsBanner">
            {disallowedNotifications ? (
                <StyledInnerContainer id="webNotificationsDenied">
                    <StyledWarningIcon color="yellow" />
                    <Text colorWhite h4>
                        <FormattedMessage id="notifications.modify_your_browser_preferences" />
                    </Text>
                    <StyledButtonLink external rel="noopener" target="_blank" url={enableBrowserNotificationsUrl}>
                        <FormattedMessage id="top_learn_more" />
                    </StyledButtonLink>
                </StyledInnerContainer>
            ) : (
                <StyledInnerContainer id="webNotificationsDefault">
                    <Text colorWhite h4>
                        <FormattedMessage id="notifications.get_notifications_when_outbid" />
                    </Text>
                    <StyledButton onClick={onClickEnableNotifications} small>
                        <FormattedMessage id="notifications.enable_browser_notifications" />
                    </StyledButton>
                </StyledInnerContainer>
            )}
            <StyledCloseIcon icon={faTimes} onClick={setCookieChecked} />
        </StyledBanner>
    );
};

export default DismissableGetWebNotificationsBanner;

const StyledBanner = styled.div`
    width: 100%;
    display: flex;
    background-color: ${({ theme }) => theme.colors.blue100};
    height: 62px;
    padding: 0 20px;
    position: sticky;
    top: 0;
    z-index: 1000;

    @media (max-width: 975px) {
        padding-right: 40px;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidthNum - 1}px) {
        display: none;
    }
`;

const StyledInnerContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
`;

const StyledWarningIcon = styled(WarningIconSmall)`
    margin-right: 16px;
`;

const buttonStyles = css`
    color: ${({ theme }) => theme.colors.white};
    border: ${({ theme }) => theme.colors.white} 1px solid;
    margin-left: 10px;

    &:hover {
        color: ${({ theme }) => theme.colors.blue200};
        border: ${({ theme }) => theme.colors.white} 1px solid;
        background: ${({ theme }) => theme.colors.white};
    }
`;

const StyledButtonLink = styled(ButtonLink)`
    ${buttonStyles}
`;

const StyledButton = styled(Button)`
    ${buttonStyles}
`;

const StyledCloseIcon = styled(FontAwesomeIcon)`
    display: block;
    position: absolute;
    top: 9px;
    right: 11px;
    z-index: 2;
    width: 17px !important;
    height: 17px;
    line-height: 1;
    color: ${({ theme }) => theme.colors.white};
    cursor: pointer;
`;
